import { useRef } from 'react';

import { useDepartmentDetails } from '~/api/query/departmentDetails';
import { ContentLoader } from '~/components/ContentLoader';
import {
  AdminUserForm,
  type AdminUserFormRef,
} from '~/components/forms/adminUser/AdminUserForm';
import { Modal } from '~/components/Modal';
import { type UserRoleValue } from '~/features/auth/roles';
import { useModal } from '~/features/modals/useModal';
import { useAdminUserCreateForm } from '~/features/userAdministration/users/hooks/useAdminUserCreateForm';
import { buildTestId } from '~/utils/buildTestId';

type AdminUserFormModalProps = {
  testID: string;
  departmentId?: string;
  idnId?: string;
  defaultRole?: UserRoleValue;
  onViewUserProfile: (userId: string) => Promise<void>;
};

export const AdminUserFormModal = ({
  testID,
  departmentId,
  idnId,
  defaultRole,
  onViewUserProfile,
}: AdminUserFormModalProps) => {
  const adminFormRef = useRef<AdminUserFormRef>(null);

  const { closeModal } = useModal();

  // Fetch department details if departmentId is provided
  const {
    data: departmentDetails,
    isLoading: isDeptDetailsLoading,
    isError: deptDetailsError,
  } = useDepartmentDetails(departmentId);

  const {
    handleSubmitClick,
    handleCreateUser,
    isFormLoading,
    isNextStepDisabled,
  } = useAdminUserCreateForm({
    formRef: adminFormRef,
    onViewUserProfile,
  });

  const displayDepartmentData = departmentId !== undefined;
  const isLoading = isDeptDetailsLoading || isFormLoading;

  return (
    <Modal
      title="Add user"
      testID={testID}
      hasCloseButton={false}
      size="m"
      primaryAction={{
        label: 'Complete',
        onPress: () => void handleSubmitClick(),
        disabled: isNextStepDisabled,
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('AdminUserForm');
        },
      }}
      onOpenChange={() => {
        closeModal('AdminUserForm');
      }}
    >
      {displayDepartmentData && isLoading && (
        <ContentLoader testID={buildTestId({ testID, suffix: 'loader' })} />
      )}

      {/* Optionally handle department details error */}
      {deptDetailsError && <div>Failed to load department details</div>}

      {/* Only show form if department details loaded (if required) */}
      {(!displayDepartmentData || departmentDetails) && (
        <AdminUserForm
          ref={adminFormRef}
          onSubmit={handleCreateUser}
          defaultValues={
            departmentDetails || idnId
              ? {
                  departmentId: departmentDetails?.id,
                  role: defaultRole,
                  idnId,
                }
              : undefined
          }
          testID={buildTestId({ testID, suffix: 'form' })}
        />
      )}
    </Modal>
  );
};
